import "../../AppSettings/headerMenus.scss";
import "./GroupedHeaderRail.scss";
import { useState, type FC } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { ActionsMenu, IconButton, SeemPopover } from "@SeemplicityDev/shared";
import DsLastSynced from "../../AppSettings/DsLastSynced/DsLastSynced";
import useIntegratedDataSources, {
  DatasourceCheckConnectionStatusEnum,
} from "../../AppSettings/Integrations/DataSources/useIntegratedDataSources";
import useHelpMenuItems from "../../AppSettings/useHelpMenuItems";
import useUserMenuItems from "../../AppSettings/useUserMenuItems";
import AppsMenu from "./AppsMenu/AppsMenu";
import SeemaNavButton from "./SeemaNavButton/SeemaNavButton";
import type { AppsMenuEntry } from "./AppsMenu/appsMenuConfig";

const SETTINGS_PATH = "/settings";

interface GroupedHeaderRailProps {
  appsMenu: AppsMenuEntry[];
  seema: { to: string } | null;
}

const GroupedHeaderRail: FC<GroupedHeaderRailProps> = ({ appsMenu, seema }) => {
  const [lastSyncedOpen, setLastSyncedOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const isSettingsActive = !!useRouteMatch(SETTINGS_PATH);
  const { integratedDataSources } = useIntegratedDataSources(false, false, {
    check_connection_statuses: DatasourceCheckConnectionStatusEnum.FAILED,
    enabled: true,
  });
  const { menuItems: userMenuItems, loggedUserLabel } = useUserMenuItems();
  const helpMenuItems = useHelpMenuItems();

  return (
    <div className="grouped-header-rail">
      {!!seema && (
        <>
          <SeemaNavButton to={seema.to} />
          <div className="grouped-header-divider" aria-hidden="true" />
        </>
      )}
      <div className="grouped-header-rail-icons">
        <SeemPopover
          isOpen={lastSyncedOpen}
          placement="bottom-end"
          body={[
            <DsLastSynced
              key="last-synced"
              close={() => setLastSyncedOpen(false)}
            />,
          ]}
          onOuterAction={() => setLastSyncedOpen(false)}
        >
          <IconButton
            data-testid="open-last-synced"
            wrapperClassName="top-menu-icon-wrap"
            className={`top-menu-icon ${lastSyncedOpen ? "is-active" : ""}`}
            isIcon="seem-icon-double-refresh"
            type="button"
            onClick={() => setLastSyncedOpen(!lastSyncedOpen)}
            isHighSeverityAlert={
              (integratedDataSources?.datasources?.edges?.length ?? 0) > 0
            }
            withTooltip={{ content: "Last Synced" }}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "last_synced", "container": "app_header"}`}
          />
        </SeemPopover>
        <AppsMenu entries={appsMenu} />
        <Link to={SETTINGS_PATH} className="top-menu-icon-link">
          <IconButton
            isIcon="seem-icon-settings"
            data-testid="settings-menu-button"
            wrapperClassName="top-menu-icon-wrap"
            className={`top-menu-icon ${isSettingsActive ? "is-active" : ""}`}
            type="button"
            withTooltip={{ content: "Settings" }}
          />
        </Link>
        <ActionsMenu
          menuItems={helpMenuItems}
          className="grouped-header-help-menu"
          data-testid="documentation-menu"
        >
          <IconButton
            isIcon="seem-icon-help"
            data-testid="doc-link-menu-button"
            wrapperClassName="top-menu-icon-wrap"
            className="top-menu-icon"
            withTooltip={{ content: "Help" }}
          />
        </ActionsMenu>
        {userMenuItems.length > 0 && (
          <ActionsMenu
            menuItems={userMenuItems}
            className="app-settings-user-menu grouped-header-user-menu"
            data-testid="app-settings-user-menu"
            onMenuOpenChange={setUserMenuOpen}
            place="bottom-end"
          >
            <IconButton
              isIcon="seem-icon-user"
              data-testid="account-menu-button"
              wrapperClassName="top-menu-icon-wrap"
              className={`top-menu-icon ${userMenuOpen ? "is-active" : ""}`}
              type="button"
              withTooltip={{ content: loggedUserLabel || "Account" }}
            />
          </ActionsMenu>
        )}
      </div>
    </div>
  );
};

export default GroupedHeaderRail;
